import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Col, Row, Container } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { createTheme, ThemeProvider } from '@mui/material';
import HelpSection from '../Quoters/Common/Appointment/HelpSection';
import { TextInput, RadioInput } from '../Helpers/Inputs';
import States from '../Helpers/Lists/States';
import { InputMaskPhone } from "../Helpers/Formatters";
import Scheduler from '../Quoters/Common/Appointment/Scheduler';
import { emptyStringToUndefined } from '../Helpers/Formatters/EmptyStringToUndefined';
import BackNext from '../Quoters/Common/SubComponents/BackNext';
import BusinessHours from '../Quoters/Common/Appointment/BusinessHours';
import { getSessionStorageItem, setSessionStorageItem, SESSION_STORAGE_POLICY_REVIEW, SESSION_STORAGE_ACCOUNT_KEY, getBasicInfo, SESSION_STORAGE_SSO_ACCESS_TOKEN_KEY, deleteConsumerApplicationId } from '../../sessionStorage';
import Age from '../Helpers/Dates/Age';
import { HeaderStepper } from '../Quoters/Common/HeaderStepper/HeaderStepper';
import InputTheme from '../Quoters/Common/Helpers/InputTheme';
import { customValidators } from '../Helpers/Validators/customValidators';
import { savedQuoteEmailDrip } from '../Quoters/Common/Helpers/SavedQuoteEmailDrip';
import { JourneyTypes } from '../Helpers/Constants/JourneyTypes';
import './Schedule.scss';

export default function Schedule() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const formStateFromSession = getSessionStorageItem(SESSION_STORAGE_POLICY_REVIEW);
  const account = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).account;
  const prefill = location && location.search && location.search.toLowerCase().includes('prefill');
  const [formState, setFormState] = useState(Schedule.DefaultFormState(prefill));
  const [loading, setLoading] = useState(false);

  const formMethods = useForm({
    resolver: yupResolver(Schedule.ValidationSchema),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: formState,
  });

  const { handleSubmit, formState: { errors } } = formMethods;

  const basicInfo = getBasicInfo();

  const handleValueChange = (field, value) => {
    setFormState({ ...formState, [field]: value });
  };

  const toBeReviewedText = () => {
    if (!formStateFromSession) {
      return '0';
    }
    const count = formStateFromSession.termPolicies.length + formStateFromSession.permPolicies.length;

    return `${count} ${count > 1 ? 'policies' : 'policy'} to be reviewed`;
  };

  const age = () => {
    if (!basicInfo) {
      return null;
    }

    return `${Age(basicInfo.birthdate)} years old`;
  };

  const fullState = () => {
    if (!basicInfo || !basicInfo.state) {
      return "";
    }

    return States().find(s => s.value === basicInfo.state).label;
  };

  const onSubmit = () => {
    setLoading(true);

    setSessionStorageItem(SESSION_STORAGE_POLICY_REVIEW, { ...formStateFromSession, schedule: formState });

    var body = {
      'consumerAccountId': account.accountId,
      'oneClickAccountId': account.oneClickAccountId,
      'dob': basicInfo.birthdate,
      'firstname': formState.firstName,
      'lastname': formState.lastName,
      'gender': basicInfo.gender,
      'state': basicInfo.state,
      'phone': formState.phone,
      'email': formState.email,
      'timeZone': formState.timeZone,
      'specificappointmentutc': formState.specificAppt === 'schedule' ? formState.specificApptTime : null,
      'policyReview': formStateFromSession,
      'isPolicyReview': true,
      'journeyType': JourneyTypes.POLICY_REVIEW
    }

    const accessToken = getSessionStorageItem(SESSION_STORAGE_SSO_ACCESS_TOKEN_KEY);
    const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;

    fetch(`${covrServicesUrl}/api/Case/PostApp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId,
        'X-Consumer-Application-ID': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).consumerApplicationId,
        'Authorization': accessToken ? 'Bearer ' + accessToken : null,
      },
      body: JSON.stringify(body),
    })
      .then(deleteConsumerApplicationId)
      .then((data) => {
        if (data.success) {
          savedQuoteEmailDrip(body.email);
          navigate('/policy-review/summary');
        }
        else {
          setLoading(false);
        }
      }).catch((error) => {
        setLoading(false);
      })
  }

  return (
    <HeaderStepper
      title="Schedule Your Policy Review"
      stepName='Schedule'
      step={3}
      totalSteps={3}
      hideContact={true}
    >
      <Container className="schedule-container">
        <FormProvider {...formMethods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="lgaModalInputs">

            <Col lg={6} className='w-100'>
              <div className='mx-5 mt-0 appointment-class py-3 px-5'>
                <h3 className="text-center title">Appointment Summary</h3>
                <h5 className="text-center subtitle mb-3">Policy Review</h5>
                <p className='bold-text'>Number of Policies</p>
                <p className='answer-text'>{toBeReviewedText()}</p>
                <Row>
                  <Col xs={6}>
                    <p className='bold-text'>Gender</p>
                    <p className='answer-text'>{(basicInfo && basicInfo.gender) && basicInfo.gender === 'M' ? 'Male' : 'Female'}</p>

                  </Col>
                  <Col xs={6}>
                    <p className='bold-text'>Age</p>
                    <p className='answer-text'>{age()}</p>

                  </Col>
                </Row>
                <p className='bold-text'>State</p>
                <p className='answer-text'>{fullState()}</p>

              </div>
              <div className="large-screen mx-5">
                <HelpSection />
              </div>
            </Col>
            <Col lg={6} className='margin-class'>
              <ThemeProvider theme={(theme) => createTheme({
                ...theme,
                ...InputTheme(),
              })}>
                <div className='mx-5'>
                  <h4 className="title mb-3">Select an appointment</h4>
                  <p className='para-text'>Your life insurance policy review will be completed over the phone with a licensed insurance specialist. During this appointment we will answer all of your questions and help you determine if your existing policy continues to meet all of your needs.</p>
                  <p className='para-text small-text'>Schedule an appointment at a convenient time or one of our insurance specialists will reach out to you within one business day.</p>
                  <Row>
                    <Col xs={6} className='pe-2'>
                      <div className='mb-3'>
                        <TextInput
                          name="firstName"
                          id="firstName"
                          value={formState.firstName}
                          onChange={(e) => handleValueChange('firstName', e.target.value)}
                          label="First Name"
                        />
                      </div>
                    </Col>
                    <Col xs={6} className='ps-2'>
                      <div className='mb-3'>
                        <TextInput
                          name="lastName"
                          id="lastName"
                          value={formState.lastName}
                          onChange={(e) => handleValueChange('lastName', e.target.value)}
                          label="Last Name"
                        />
                      </div>
                    </Col>
                  </Row>
                  <div className='mb-3'>
                    <TextInput
                      name="email"
                      id="email"
                      value={formState.email}
                      onChange={(e) => handleValueChange('email', e.target.value)}
                      label="Email"
                    />
                  </div>
                  <div className="mb-1">
                    <TextInput
                      name="phone"
                      id="phone"
                      value={formState.phone}
                      onChange={(e) => handleValueChange('phone', e.target.value)}
                      label="Phone Number"
                      InputProps={{
                        inputComponent: InputMaskPhone,
                      }}
                    />
                  </div>
                </div>
                <div className='mb-0 mx-5'>
                    <RadioInput
                      name="specificAppt"
                      id="specificAppt"
                      label=""
                      value={formState.specificAppt}
                      hideError
                      radios={[{ value: "schedule", label: 'Schedule an appointment' }]}
                      onChange={e => handleValueChange('specificAppt', e.target.value)}
                  />
                </div>
                <div className='mx-5 mb-3'>
                  <RadioInput
                    name="specificAppt"
                    id="specificAppt"
                    label=""
                    value={formState.specificAppt}
                    radios={[{ value: "asap", label: "Contact me within one business day" }]}
                    onChange={e => handleValueChange('specificAppt', e.target.value)}
                    />
                  </div>
              </ThemeProvider>

              <div className='mx-5'>
                <Scheduler
                  hide={formState.specificAppt !== 'schedule'}
                  hideBusinessHours={true}
                  errors={errors}
                  timeZone={formState.timeZone}
                  apptDate={formState.specificApptDate}
                  apptTime={formState.specificApptTime}
                  setTimeZone={(e) => handleValueChange('timeZone', e)}
                  setApptDate={(e) => handleValueChange('specificApptDate', e)}
                  setApptTime={(e) => handleValueChange('specificApptTime', e)}
                />
                <BusinessHours />
                <BackNext onBack={() => navigate('/policy-review/existing-insurance')} continueLabel={{ text: 'Submit' }} loading={loading}/>
              </div>
            </Col>
          </Row>
        </Form>
        </FormProvider>
      </Container>
    </HeaderStepper>
  );
}

Schedule.DefaultFormState = (prefill = false) => {
  return {
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    specificAppt: '',
    specificApptDate: '',
    specificApptTime: '',
    timeZone: ''
  }
};

Schedule.ValidationSchema = object().shape({
  email: string()
    .email('Email address must be valid.')
    .required('Email is required'),
  firstName: customValidators.string().isValidNameWithFiftyChar('First Name is required.', true).required('First Name is required.'),
  lastName: customValidators.string().isValidNameWithFiftyChar('Last Name is required.', true).required('Last Name is required.'),
  phone: string()
    .required('Phone number is required.')
    .matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, 'Phone must be formatted as (123) 456-7890'),
  timeZone: string().transform(emptyStringToUndefined).when('specificAppt', {
    is: 'schedule',
    then: (schema) => schema.required("Timezone is required."),
  }),
  specificAppt: string()
    .required('Select an appointment option.'),
  specificApptDate: string().transform(emptyStringToUndefined).when('specificAppt', {
    is: 'schedule',
    then: (schema) => schema.required("Date is required."),
  }),
  specificApptTime: string()
    .when('specificAppt', {
      is: 'schedule',
      then: (schema) => schema.required('Time of call is required.'),
      otherwise: (schema) => schema.notRequired()
    }),
});
